import { formatDate } from 'date-fns';
import type { UpdateSubscriptionData, UpdateTeamDataType } from '~/lib/stores/team';
import type { OrganizationMembership } from '~/types/organization';
import type { Subscription } from '~/types/subscription';
import { compileTimeUnreachable } from '~/utils/unreachable';

export const calculatePrice = ({
  seats,
  planPrice,
  isYearly,
}: {
  seats: number;
  planPrice: number;
  isYearly: boolean;
}) => seats * planPrice * (isYearly ? 12 : 1);

export const calculateTotalPrice = ({
  newMembersCount,
  oldMembersCount,
  planPrice,
  isYearly,
}: {
  newMembersCount: number;
  oldMembersCount: number;
  planPrice: number;
  isYearly: boolean;
}) =>
  Math.abs(
    calculatePrice({ seats: newMembersCount, planPrice, isYearly }) -
      calculatePrice({ seats: oldMembersCount, planPrice, isYearly }),
  ).toFixed(2);

export const getMembersAfterUpdate = (updateData: UpdateSubscriptionData, orgMembers: OrganizationMembership[]) => {
  const oldMembers = orgMembers.filter((m) => m.withMembership);

  if (updateData.type === 'upgrade') {
    return [...oldMembers, ...updateData.members];
  }

  if (['downgrade', 'remove'].includes(updateData.type)) {
    return oldMembers.filter((member) => !updateData.members.some((m) => m.user.username === member.user.username));
  }

  return oldMembers;
};

export const getHeaderText = (type: UpdateTeamDataType) => {
  switch (type) {
    case 'upgrade': {
      return 'Upgrade confirmation';
    }
    case 'downgrade': {
      return 'Downgrade confirmation';
    }
    case 'remove': {
      return 'Confirm removal from team';
    }
    case 'change_role': {
      return 'Role change confirmation';
    }
    default: {
      return 'Confirmation';
    }
  }
};

export const getButtonText = (type: UpdateTeamDataType, subscription: Subscription) => {
  if (type === 'upgrade') {
    return 'Checkout now';
  } else if (type === 'downgrade') {
    if (subscription.interval === 'yearly' && !subscription.currentPeriodEnd) {
      return 'Switch on the next billing period';
    }

    return `Switch on ${formatDate(
      subscription.interval === 'yearly'
        ? (subscription.currentPeriodEnd as number)
        : subscription.nextMonthStartTimestamp,
      'MMMM dd, yyyy',
    )}`;
  } else {
    return 'Confirm change';
  }
};

export const getDescriptionText = (type: UpdateTeamDataType, newRole?: string) => {
  switch (type) {
    case 'upgrade': {
      return 'Paid membership allows to prompt on Bolt';
    }
    case 'downgrade': {
      return 'Downgrading users will exclude them from the team subscription';
    }
    case 'remove': {
      return 'These users will immediately lose access to Bolt';
    }
    case 'change_role': {
      return (
        <>
          These users will receive <span className="capitalize font-semibold">{newRole?.replaceAll('_', ' ')}</span>{' '}
          role
        </>
      );
    }
    default: {
      compileTimeUnreachable(type);
    }
  }
  return null;
};
